import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt, formatNumber } from '@miq/utils';
import { DataObject, ReportRecord } from '../../../models/report.model';
import { TemplateFieldsDisplayFn } from '../../../models/template.model';
import { sortAttributesByFieldId } from '../utils/argument-sorting';
import { Dash, displayFnWrapper, mapAndFormatDynamicValue } from '../utils/dynamic-data-retrievers';
import { getFloorAndSuite, getFloorPlanImageSlide } from './common';
import { STACKINGPLAN_FIELDS } from './stacking-plan-slide-common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  ...STACKINGPLAN_FIELDS(),
  27: {
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
    routeFn: ({ data: entityRecord }: ReportRecord) => {
      return ['/records', 'property', digger(entityRecord, 'property.id'), 'details'];
    },
  },
  49: {
    displayFn: (record: ReportRecord, _options, arg1, arg2) => {
      const values = concatValuesBy(
        defaultTxt,
        mapAndFormatDynamicValue(record, arg1),
        mapAndFormatDynamicValue(record, arg2)
      );
      return values;
    },
  },
  60: {
    displayFn: displayFnWrapper,
  },
  61: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  62: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  63: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  627: {
    displayFn: (record: ReportRecord, _options, arg1, arg2) => {
      return concatValuesBy(' & ', mapAndFormatDynamicValue(record, arg1), mapAndFormatDynamicValue(record, arg2));
    },
  },
  64: {
    displayFn: (record: ReportRecord, _options, arg1, arg2) => {
      if (Array.isArray(record.data)) {
        const result = record.data.map(element => {
          const floor = mapAndFormatDynamicValue({ ...record, data: element }, arg1);
          const isWholeFloor = mapAndFormatDynamicValue({ ...record, data: element }, arg2);
          return (floor as string).trim() + '/' + (isWholeFloor === 'Yes' ? 'Whole Floor' : 'Part');
        });
        return concatValuesBy(',', ...new Set(result));
      }
      return mapAndFormatDynamicValue(record, arg1);
    },
  },
  65: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  66: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  67: {
    displayFn: (record: ReportRecord, _options, arg) => {
      if (Array.isArray(record.data)) {
        const { entity, field, id } = arg;
        let total = 0;
        let totalComposition = [];
        record.data.forEach(avail => {
          const askingRentVal = mapAndFormatDynamicValue(
            { ...record, data: avail },
            { dataType: 'id', entity, field, id }
          );
          total = total + Number(askingRentVal[0]);
          totalComposition = [...totalComposition, formatNumber(askingRentVal[0], undefined, arg.dataFormat)];
        });
        const result =
          formatNumber(total, undefined, arg.dataFormat) + ` SF(${concatValuesBy('+', ...totalComposition)})`;
        return result;
      }
      return mapAndFormatDynamicValue(record, arg);
    },
  },
  68: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  69: {
    displayFn: displayFnWrapper,
  },
  70: {
    displayFn: (record: ReportRecord) => {
      let frontageVal: string;
      try {
        const recordValue = Array.isArray(record.data) ? record.data[0] : record.data;
        const availabilityUsageType = (recordValue as DataObject).property.property_usage.getUsageType() || '';
        const propertyUsage = record.associatedProperty.usages.find(
          usage => (usage.getUsageType() || '').toLowerCase() === availabilityUsageType.toLowerCase()
        );
        frontageVal = (propertyUsage || {}).frontage_length;
      } catch {
        frontageVal = '-';
      }

      return frontageVal;
    },
  },
  626: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  72: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  73: {
    displayFn: displayFnWrapper,
  },
  74: {
    displayFn: displayFnWrapper,
  },
  75: {
    displayFn: displayFnWrapper,
  },
  76: {
    displayFn: displayFnWrapper,
  },
  77: {
    displayFn: (record: ReportRecord, _options, powerBackup) => {
      let val: string;
      try {
        const availabilityUsageType = (record.data as DataObject).property.property_usage.getUsageType() || '';
        const propertyUsage = record.associatedProperty.usages.find(
          usage => (usage.getUsageType() || '').toLowerCase() === availabilityUsageType.toLowerCase()
        );
        val = digger({ ...{ usages: [propertyUsage] } }, powerBackup.path);
      } catch {
        val = '-';
      }
      return val != null ? (val === 'Y' ? 'YES' : 'NO') : defaultTxt;
    },
  },
  850: {
    displayFn: (record: ReportRecord) => {
      return digger(record.associatedProperty, 'property_name') || '-';
    },
  },
  ...getFloorPlanImageSlide(),
  ...getFloorAndSuite(),
};
